import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ConfigConstant } from '@shared/constants/config.constant';
import { NotificationItem } from '@features/notifications/models/notification-item.model';
import { NotificationService } from '@features/notifications/services/notification.service';
import { RoutesConstants } from '@shared/constants';
import { NotificationType } from '@shared-features/enums/shared-enums.enum';
import { BidChatDrawerService } from '@features/bid-chat/services/bid-chat-drawer.service';
import { BID_CHAT_PUSH, offerPreviewPrice } from '@features/bid-chat/constants/bid-chat.constants';
import { BidChatService } from '@features/bid-chat/services/bid-chat.service';
import { BidChatRow } from '@features/bid-chat/models/bid-chat.model';

/** How long a chat message's live update and its push are matched to each other. */
const CHAT_MESSAGE_PAIR_WINDOW_MS = 30000;

@Injectable()
export class MessagingService implements OnDestroy {
  public currentMessage$ = new BehaviorSubject(null);
  public notificationToken$ = new BehaviorSubject(ConfigConstant.INIT_VALUE);

  private destroy$ = new Subject();
  /**
   * Per conversation, chat messages seen from one source (live listener or push) and still waiting for
   * the other one: whichever comes first toasts, and its pair is then dropped.
   */
  private unpairedChatMessages = new Map<string, { isLive: boolean; at: number }[]>();

  constructor(
    private angularFireMessaging: AngularFireMessaging,
    private notificationService: NotificationService,
    private bidChatDrawerService: BidChatDrawerService,
    private bidChatService: BidChatService,
    private translateService: TranslateService
  ) {
    // The live listener sees a chat message seconds before its push: toast and reload from it at once.
    this.bidChatService.incomingMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((row) => this.onChatMessage(this.chatNotification(row), true));
  }


  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  requestPermission(): Promise<void> {
    const Notification = window.Notification || null;
    if (Notification &&
      Notification?.requestPermission &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    ) {
      return new Promise((resolve) => {
        this.angularFireMessaging.requestPermission
          .pipe(take(1))
          .subscribe((val) => {
            this.getToken();
            resolve();
          }, (err) => {
            this.notificationToken$.next(null);
            resolve();
          });
      });
    }
    this.notificationToken$.next(RoutesConstants.NOT_SUPPORTED);
    return Promise.resolve();
  }

  getToken(): void {
    // Offline, Firebase rejects the registration with an unhandled `app-offline` and never
    // retries: fetch it once the browser is back online. No token is emitted meanwhile, so
    // subscribers keep waiting past `INIT_VALUE` and register the real token.
    if (!navigator.onLine) {
      fromEvent(window, 'online')
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(() => this.getToken());
      return;
    }

    this.angularFireMessaging.getToken.pipe(
      takeUntil(this.destroy$),
    ).subscribe(
      (token) => {
        this.notificationToken$.next(token);
        this.receiveMessage();
      },
      (err) => {
        console.error('Unable to get permission to notify.', err);
        this.notificationToken$.next(RoutesConstants.NOT_SUPPORTED);
        this.notificationToken$.next(null);
      }
    );
  }

  receiveMessage(): void {
    this.angularFireMessaging.messages.pipe(takeUntil(this.destroy$)).subscribe(
      (payload) => {
        const data = payload.data || {};
        const notification = new NotificationItem();
        notification.type = (data.type || data.notificationType) as NotificationType;
        // A chat push may not be a stored notification: it still needs an id, or the toast drops
        // every message after the first as a duplicate.
        notification.id = data.notificationId || `${notification.type}-${Date.now()}`;
        notification.title = payload.notification?.title || data.title;
        notification.body = payload.notification?.body || data.body;
        // A chat push carries `shipmentId` (chat contract §11); its `id`, if any, isn't the shipment.
        const elementId = notification.type === NotificationType.Chat ? data[BID_CHAT_PUSH.shipmentIdKey] : data.id;
        notification.elementId = +elementId || null;
        notification.conversationId = data[BID_CHAT_PUSH.conversationIdKey];
        notification.createdAt = Date.now();
        this.currentMessage$.next(payload);
        if (notification.type === NotificationType.Chat) {
          this.onChatMessage(notification);
          return;
        }
        this.notificationService.showNotification(notification);
      });
  }

  /**
   * A chat message from the live listener or its push: reload that shipment's bids (again on the push,
   * which may come after the new price is saved), then toast once per message.
   */
  private onChatMessage(notification: NotificationItem, isLive = false): void {
    const { conversationId, elementId: shipmentId } = notification;
    this.bidChatDrawerService.chatMessageReceived({ shipmentId, conversationId });
    // The other source already handled this message: toasted it, or hid it while the chat was open.
    if (conversationId && this.pairChatMessage(conversationId, isLive)) {
      return;
    }
    // Already reading that chat: the message is on screen, so no toast (chat contract §11).
    if (this.bidChatDrawerService.isShowingChat({ conversationId, shipmentId })) {
      return;
    }
    this.notificationService.showNotification(notification);
  }

  /** Drops this message's pair from the other source if one is waiting (true), else records it to wait. */
  private pairChatMessage(conversationId: string, isLive: boolean): boolean {
    const now = Date.now();
    const unpaired = (this.unpairedChatMessages.get(conversationId) || []).filter(({ at }) => now - at < CHAT_MESSAGE_PAIR_WINDOW_MS);
    const pairIndex = unpaired.findIndex((message) => message.isLive !== isLive);
    if (pairIndex >= 0) {
      unpaired.splice(pairIndex, 1);
    } else {
      unpaired.push({ isLive, at: now });
    }
    if (unpaired.length) {
      this.unpairedChatMessages.set(conversationId, unpaired);
    } else {
      this.unpairedChatMessages.delete(conversationId);
    }
    return pairIndex >= 0;
  }

  private chatNotification(row: BidChatRow): NotificationItem {
    const price = offerPreviewPrice(row.lastMessagePreview);
    const name = row.counterpartName;
    const notification = new NotificationItem();
    notification.type = NotificationType.Chat;
    notification.id = `${NotificationType.Chat}-${row.firestoreConversationId}-${row.lastMessageAt}`;
    notification.title = this.translateService.instant(price === null ? 'bidChat.NewMessageFrom' : 'bidChat.NewOfferFrom', { name });
    notification.body = price === null ? row.lastMessagePreview : this.translateService.instant('bidChat.OfferToastBody', { price });
    notification.elementId = row.shipmentId;
    notification.conversationId = row.firestoreConversationId;
    notification.createdAt = Date.now();
    return notification;
  }
}
